import type {
  TeaserBaseProps,
  TeaserMediaProps,
  TeaserTextProps,
  TeaserVignetteProps,
} from '@bonniernews/dn-design-system-web/assets/types/teaser-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'
import { Body, Content, Media, Title } from '@bonniernews/dn-design-system-web/helpers/teaser.tsx'

import { TeaserCard } from '@bonniernews/dn-design-system-web/components/teaser-card/teaser-card.tsx'

interface TeaserNativeProps extends TeaserBaseProps, TeaserMediaProps, TeaserTextProps, TeaserVignetteProps {
  nativeVariant?: 'standard' | 'large' | 'right'
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/teaser-native](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/teaser-tipsa)
 * - Storybook: [TeaserNative](https://designsystem.dn.se/?path=/docs/section-teasertnative--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/teaser-native/teaser-native.scss'`
 *
 */
export const TeaserNative = ({
  targetLink,
  areaType,
  title,
  text,
  media,
  vignette,
  nativeVariant = 'standard',
  classNames,
  attributes = {},
}: TeaserNativeProps) => {
  const componentClassName = 'ds-teaser'
  const classes = formatClassString([
    componentClassName,
    'ds-teaser--native',
    `ds-teaser--native-${nativeVariant}`,
    classNames,
  ])

  return (
    <TeaserCard
      areaType={areaType}
      targetLink={targetLink}
      classNames={classes}
      attributes={{ ...attributes, rel: targetLink && isAbsoluteUrl(targetLink) ? 'sponsored' : undefined }}
    >
      <div className='ds-teaser__native-ad-bar'>
        <span className='ds-teaser__native-ad-bar__label'>Annons</span>
        <span className='ds-teaser__native-ad-bar__description'>{vignette}</span>
      </div>

      {nativeVariant !== 'standard' && <Media media={media} />}
      <Content>
        {nativeVariant === 'standard' && <Media media={media} rounded={true} />}
        <Title title={title} />
        {nativeVariant !== 'right' && <Body text={text} />}
      </Content>
    </TeaserCard>
  )
}

function isAbsoluteUrl(url: string) {
  return /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(url)
}
